import React, { useState, useEffect } from 'react'
import {
  Alert,
  LoadingSpinner,
  FormField,
} from '@CommonComponents'
import { client } from '@Utils'
import { OrderSummary } from './OrderSummary.js'
import { Confirmation } from './Confirmation.js'
import { Select } from './Select.js'
import { CommentBox } from './CommentBox.js'

export const OrderInfo = () => {
  const [courses, setCourses] = useState([])
  const [numberOfCodes, setNumberOfCodes] = useState(0)
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [institution, setInstitution] = useState('')
  const [course_identifier, setCourseIdentifier] = useState('')
  const [pOnumber, setPoNumber] = useState('')
  const [book, setBook] = useState({ id: '-1', title: '________', price: 0 })
  const [formErrorMsg, setFormErrorMsg] = useState('')
  const [comments, setComments] = useState('')
  const [loading, setLoading] = useState(false)
  const [finished, setFinished] = useState(false)
  const [customerEmailSent, setCustomerEmailSent] = useState(false)

  useEffect(() => {
    const getCourses = async () => {
      let url = '/homepage/orderaccesscodes.get_courses/'
      try {
        var response = await client.get(url)
      } catch (err) {
        console.error(`Error fetching ${url}`, err)
        return
      }
      setCourses(response.data.courses)
    }
    getCourses()
  }, [])

  const handleNumberChange = event => {
    let number = parseInt(event.target.value)
    if (number < 0) {
      number = 0
    }
    setNumberOfCodes(number)
  }

  const handleBookChange = event => {
    let newBook
    for (let i = 0; i < courses.length; i++) {
      if (courses[i].id === event.target.value) {
        newBook = courses[i]
        break
      }
    }
    setBook(newBook)
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (book.id === '-1') {
      setFormErrorMsg('You must select a book.')
      return
    }

    if (numberOfCodes < 1) {
      setFormErrorMsg('You must order at least 1 code.')
      return
    }

    if (contactName === '') {
      setFormErrorMsg('Contact Name cannot be blank.')
      return
    }

    if (email === '') {
      setFormErrorMsg('Email cannot be blank.')
      return
    }

    if (institution === '') {
      setFormErrorMsg('Institution cannot be blank.')
      return
    }

    setLoading(true)
    setFormErrorMsg('')
    let url = '/homepage/orderaccesscodes.handle_form_data/'
    let response
    try {
      response = await client.post(
        url,
        {
          numberOfCodes,
          contactName,
          email,
          institution,
          course_identifier,
          pOnumber,
          book,
          comments,
        },
        // Belt-and-suspenders: only treat a genuine 2xx as a resolved response.
        // (In the browser XHR follows 3xx transparently, so axios usually only
        // sees the final status anyway — the real guard against a followed
        // redirect to an HTML login/maintenance page is the ok:true check below.)
        { validateStatus: status => status >= 200 && status < 300 }
      )
    } catch (err) {
      console.error(`Error posting ${url}`, err)
      setFormErrorMsg(
        'An unknown server error occurred. Contact support@myeducator.com'
      )
      setLoading(false)
      return
    }

    // The server must explicitly confirm the order was recorded. Without this
    // guard, any resolved response (e.g. a redirect the browser transparently
    // followed to a 200 page) would show a false "confirmed" even though no
    // AccessCodeOrder was created and no email was sent.
    if (!response || !response.data || response.data.ok !== true) {
      console.error(`Unexpected response from ${url}`, response)
      setFormErrorMsg(
        'We could not confirm your order was received. Please contact ' +
          'support@myeducator.com before trying again so we can avoid a ' +
          'duplicate order.'
      )
      setLoading(false)
      return
    }

    setCustomerEmailSent(response.data.customer_email_sent === true)
    setFinished(true)
  }

  if (finished) {
    return <Confirmation email={email} emailSent={customerEmailSent} />
  }

  if (loading) {
    return <h4 className="text-dark-blue">Loading...</h4>
  }

  let hasCatalogCourses = courses && courses.length !== 0
  let selectHtml = hasCatalogCourses ? (
    <Select courses={courses} handler={handleBookChange} book={book} />
  ) : (
    <LoadingSpinner />
  )

  let total = (numberOfCodes * book.price).toFixed(2)

  let msg =
    formErrorMsg === '' ? null : (
      <Alert bootstrapClass="danger" message={formErrorMsg} />
    )

  return (
    <div>
      {msg}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Book</label>
          {selectHtml}
        </div>
        <FormField
          name="numberOfCodes"
          label="Number of Access Codes"
          value={numberOfCodes}
          handler={handleNumberChange}
          type="number"
        />
        <FormField
          name="contact"
          label="Contact Name"
          value={contactName}
          handler={e => setContactName(e.target.value)}
          type="text"
        />
        <FormField
          name="email"
          label="Email"
          value={email}
          handler={e => setEmail(e.target.value)}
          type="text"
        />
        <FormField
          name="institution"
          label="Institution"
          value={institution}
          handler={e => setInstitution(e.target.value)}
          type="text"
          helpText="Access codes are specific to the university bookstore that orders them and cannot be transferred to other universities."
        />
        <FormField
          name="course_identifier"
          label="Instructor/Course Identifier (optional)"
          value={course_identifier}
          handler={e => setCourseIdentifier(e.target.value)}
          type="text"
          helpText="Used to distinguish between different courses using the same resource"
        />
        <FormField
          name="ponumber"
          label="PO Number (optional)"
          value={pOnumber}
          handler={e => setPoNumber(e.target.value)}
          type="text"
        />
        <CommentBox
          name="comments"
          label="Comments (optional)"
          row="3"
          value={comments}
          handler={e => setComments(e.target.value)}
          placeholder="If you have additional questions or comments, you can list them here."
          type="text"
        />
        <OrderSummary
          bookTitle={book.title}
          numberOfCodes={numberOfCodes}
          bookPrice={book.price}
          total={total}
        />
        <button className="btn btn-tomato" type="submit">
          Submit
        </button>
      </form>
    </div>
  )
}
